import { useEffect, useState } from 'react'
import { UAParser } from 'ua-parser-js'
import { getUser, useSelector } from '../store'

function compareVersion(v1: any, v2: any) {
  for (let i = 0; i < Math.max(v1.length, v2.length); i++) {
    const num1 = v1[i] || 0
    const num2 = v2[i] || 0
    if (num1 > num2) return 1
    if (num1 < num2) return -1
  }
  return 0
}

function normalizeBrowserName(name: string) {
  name = name.toLowerCase()
  if (name.includes('chrome') && !name.includes('edge')) return 'chrome'
  if (name.includes('brave')) return 'brave'
  if (name.includes('edge')) return 'edge'
  if (name.includes('safari') && !name.includes('chrome')) return 'safari'
  return name
}

export function usePrfSupport() {
  const [isSupported, setIsSupported] = useState(false)
  const [deviceInfo, setDeviceInfo] = useState<{
    os: string
    osVersion: string
    browser: string
    browserVersion: string
  } | null>(null)
  const [loading, setLoading] = useState(true)
  const user = useSelector(getUser)

  useEffect(() => {
    async function checkSupport() {
      let _os = 'unknown'
      let _osVersion = []
      let _browser = 'unknown'
      let _browserVersion = []

      if (typeof window === 'undefined') return

      // @ts-ignore
      if (navigator?.userAgentData) {
        // @ts-ignore
        const ua = await navigator.userAgentData.getHighEntropyValues([
          'platform',
          'platformVersion',
          'brands',
        ])
        console.log('ua', ua)

        // OS
        _os = ua.platform.toLowerCase()
        _osVersion = ua.platformVersion
          .split('.')
          .map((num: any) => parseInt(num, 10))

        // Browser
        const brand = ua.brands
          .map((b: { brand: string; version: string }) => ({
            brand: normalizeBrowserName(b.brand),
            version: b.version,
          }))
          .find((b: any) =>
            ['chrome', 'safari', 'edge', 'brave'].includes(b.brand),
          )

        if (brand) {
          _browser = brand.brand
          _browserVersion = brand.version
            .split('.')
            .map((num: any) => parseInt(num, 10))
        }
      } else {
        // fallback sur userAgent classique si nécessaire
        const uaString = navigator.userAgent
        const parser = new UAParser(uaString)
        const result = parser.getResult()

        // OS
        const osName = (result.os.name || 'unknown').toLowerCase()
        if (osName.includes('mac')) {
          _os = 'macos'
        } else if (osName.includes('win')) {
          _os = 'windows'
        } else if (osName.includes('android')) {
          _os = 'android'
        } else if (
          osName.includes('ios') ||
          osName.includes('iphone') ||
          osName.includes('ipad')
        ) {
          _os = 'ios'
        } else {
          _os = 'unknown'
        }
        _osVersion = (result.os.version || '')
          .split('.')
          .filter((v: any) => v !== '')
          .map((v: any) => parseInt(v, 10))

        // Browser
        _browser = normalizeBrowserName(result.browser.name || 'unknown')
        _browserVersion = (result.browser.version || '')
          .split('.')
          .filter((v: any) => v !== '')
          .map((v: any) => parseInt(v, 10))
      }

      let supported = false

      if (_os === 'macos' && compareVersion(_osVersion, [15]) >= 0) {
        supported =
          (_browser === 'chrome' &&
            compareVersion(_browserVersion, [130]) >= 0) ||
          (_browser === 'brave' &&
            compareVersion(_browserVersion, [130]) >= 0) ||
          _browser === 'safari'
      } else if (_os === 'windows') {
        supported = false
      } else if (_os === 'android' && compareVersion(_osVersion, [12]) >= 0) {
        supported =
          (_browser === 'chrome' &&
            compareVersion(_browserVersion, [130]) >= 0) ||
          (_browser === 'edge' && compareVersion(_browserVersion, [130]) >= 0)
      } else if (_os === 'ios' && compareVersion(_osVersion, [18, 4]) >= 0) {
        supported =
          (_browser === 'chrome' &&
            compareVersion(_browserVersion, [130]) >= 0) ||
          (_browser === 'safari' &&
            compareVersion(_browserVersion, [18]) >= 0) ||
          (_browser === 'brave' && compareVersion(_browserVersion, [1]) >= 0) ||
          (_browser === 'edge' && compareVersion(_browserVersion, [1]) >= 0) ||
          (_browser === 'mobile firefox' &&
            compareVersion(_browserVersion, [1]) >= 0)
      }

      console.log('Prf support: ', supported ? 'supported' : 'not supported')
      console.log('OS: ', _os, _osVersion)
      console.log('Browser: ', _browser, _browserVersion)

      setDeviceInfo({
        os: _os,
        osVersion: _osVersion.join('.') || 'unknown',
        browser: _browser,
        browserVersion: _browserVersion.join('.') || 'unknown',
      })
      setIsSupported(supported)
      setLoading(false)
    }

    checkSupport()
  }, [user])

  return { isSupported, deviceInfo, loadingPrfSupport: loading }
}
